import React, { useState } from 'react'
import classNames from 'classnames'
import V1 from './v1'
import V2 from './v2'
import { Button } from '@deal/components'
import SidebarAwareGrid from '#src/components/SidebarAwareGrid'
import { generateGrid } from '#src/services/grid'
import { Link } from 'react-router-dom'
import { ExpertSellableNotesComponent, ProductExpertFragment } from '#src/generated/graphql'
import { generateCreateUrlFromCategory } from '#src/services/path'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import { Experiment, Variation } from '@deal/experiment-js'
import IconQuote from './icon-quote.svg'
import styles from './styles.css'

interface ExpertNotesProps {
  sellableId: string
  categorySlug: string
  maybeSellableExpert?: ProductExpertFragment | null
  hasRequestForDepartment: boolean
}

const ExpertNotes: React.SFC<ExpertNotesProps & WithAnalyticsProps> = ({
  sellableId,
  analytics,
  categorySlug,
  maybeSellableExpert,
  hasRequestForDepartment
}) => {
  const [wasFeedbackGiven, setWasFeedbackGiven] = useState(false)

  const notesDefault = generateGrid([
    {
      count: 2
    },
    {
      note: 2
    },
    {
      actions: 2
    },
    {
      helpful: 2
    }
  ])

  const notesMd = generateGrid([
    {
      count: 6,
      note: 6
    },
    {
      actions: 6,
      note: 6
    },
    {
      helpful: 12
    }
  ])

  return (
    <ExpertSellableNotesComponent
      variables={{
        query: {
          sellableId
        }
      }}
    >
      {({ data }) => {
        /**
         * No loading state here because most sellables will not have notes
         */
        if (
          data &&
          data.suggestExpertCuratedItemNotes &&
          data.suggestExpertCuratedItemNotes.edges.length
        ) {
          const expertNoteNode = data.suggestExpertCuratedItemNotes.edges[0].node

          return (
            <div
              className={classNames(styles.wrapper, {
                [styles.noteDismissed]: wasFeedbackGiven
              })}
            >
              <SidebarAwareGrid areas={notesDefault} areasMd={notesMd}>
                {({ Count, Note: NoteArea, Actions, Helpful }) => {
                  return (
                    <>
                      <Count className={styles.count}>
                        <div className={styles.icon}>
                          <IconQuote className={styles.quoteIcon} />
                        </div>
                        <div className={styles.header}>
                          Recommended{' '}
                          <span className={styles.underline}>{expertNoteNode.useCount}</span> times
                          by our experts
                        </div>
                      </Count>
                      <NoteArea>
                        <Experiment id="force-expert-on-notes" default="on">
                          <Variation id="on">
                            <V1
                              categorySlug={categorySlug}
                              note={expertNoteNode}
                              maybeSellableExpert={maybeSellableExpert}
                            />
                          </Variation>
                          <Variation id="off">
                            <V2
                              note={expertNoteNode}
                              maybeSellableExpert={expertNoteNode.createdBy}
                            />
                          </Variation>
                        </Experiment>
                      </NoteArea>

                      <Actions>
                        {!hasRequestForDepartment && (
                          <div className={styles.actions}>
                            <span>Looking for a personalized curation?</span>
                            <div>
                              <Link
                                to={generateCreateUrlFromCategory({
                                  maybeCategorySlug: categorySlug,
                                  sourceKey: 'product-details-expert-notes-quiz-button',
                                  pathIntent: 'request'
                                })}
                              >
                                <Button variant="secondary-link" className={styles.quizButton}>
                                  Take our 1 min quiz →
                                </Button>
                              </Link>
                            </div>
                          </div>
                        )}
                      </Actions>

                      <Helpful>
                        {!wasFeedbackGiven && (
                          <div className={styles.helpful}>
                            Did you find this helpful?
                            <div
                              className={styles.helpfulButton}
                              onClick={() => {
                                if (analytics) {
                                  analytics.events.expertNoteHelpful({
                                    sellable_id: sellableId,
                                    note_id: expertNoteNode.id
                                  })
                                }

                                setWasFeedbackGiven(true)
                              }}
                            >
                              Yes
                            </div>
                            <div
                              className={styles.helpfulButton}
                              onClick={() => {
                                if (analytics) {
                                  analytics.events.expertNoteNotHelpful({
                                    sellable_id: sellableId,
                                    note_id: expertNoteNode.id
                                  })
                                }

                                setWasFeedbackGiven(true)
                              }}
                            >
                              No
                            </div>
                          </div>
                        )}
                      </Helpful>
                    </>
                  )
                }}
              </SidebarAwareGrid>
            </div>
          )
        }

        return null
      }}
    </ExpertSellableNotesComponent>
  )
}

export default withAnalytics(ExpertNotes)
